import React, { useState, useCallback } from 'react';
import {
  ChevronDown,
  Sparkles,
  ListOrdered,
  Zap,
  HelpCircle,
  ArrowUpRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import {
  homepageFaqs,
  homepageFeatureCards,
  homepageUseCases,
  homepageHowItWorks,
} from '../seo/homepageSeoData';

const SeoSection = ({ id, icon: Icon, title, isOpen, onToggle, children }) => {
  const panelId = `${id}-panel`;

  return (
    <div className="bg-card border border-border rounded-xl">
      <div
        className="flex items-center justify-between p-4 cursor-pointer group hover:bg-secondary/50 transition-colors duration-200 focus:outline-none focus:ring-0"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <div className="flex items-center space-x-3">
          <Icon className="w-5 h-5 text-primary" aria-hidden />
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        </div>
        <ChevronDown
          className={`w-5 h-5 transition-colors duration-200 text-muted-foreground group-hover:text-[#edff00] ${
            isOpen ? '' : 'rotate-[-90deg]'
          }`}
          aria-hidden
        />
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={panelId}
            role="region"
            aria-labelledby={id}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SeoLandingContent = () => {
  const [openSections, setOpenSections] = useState({});
  const [openFaq, setOpenFaq] = useState(null);

  const toggleSection = useCallback(
    (id) => setOpenSections((prev) => ({ ...prev, [id]: !prev[id] })),
    [],
  );

  const toggleFaq = useCallback(
    (idx) => setOpenFaq((prev) => (prev === idx ? null : idx)),
    [],
  );

  return (
    <section
      className="mt-10 md:mt-14"
      aria-labelledby="invoice-generator-overview"
    >
      <div className="space-y-4">
        {/* Hero intro card */}
        <div className="bg-card border border-border rounded-xl p-6">
          <p className="text-sm font-medium uppercase tracking-[0.18em] text-primary mb-3">
            Free Online Invoice Generator
          </p>
          <h2
            id="invoice-generator-overview"
            className="text-2xl sm:text-3xl font-bold text-foreground leading-tight"
          >
            Create professional invoices and download PDF invoices in minutes
          </h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground max-w-3xl">
            Invo is a free online invoice generator built for freelancers, consultants,
            agencies, and small businesses. Create invoices in your browser, customize
            professional templates, add taxes and payment terms, preview your invoice
            live, and download a polished PDF — no sign-up required.
          </p>
        </div>

        {/* Features */}
        <SeoSection
          id="features-section"
          icon={Sparkles}
          title="Features"
          isOpen={!!openSections['features-section']}
          onToggle={() => toggleSection('features-section')}
        >
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {homepageFeatureCards.map((card) => (
              <div
                key={card.title}
                className="rounded-lg border border-border p-4"
                style={{ backgroundColor: 'var(--input)' }}
              >
                <h3 className="text-sm font-medium text-foreground">
                  {card.title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                  {card.description}
                </p>
              </div>
            ))}
          </div>
        </SeoSection>

        {/* How it works */}
        <SeoSection
          id="how-to-create-invoice"
          icon={ListOrdered}
          title="How to Create an Invoice"
          isOpen={!!openSections['how-to-create-invoice']}
          onToggle={() => toggleSection('how-to-create-invoice')}
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {homepageHowItWorks.map((item) => (
              <div
                key={item.step}
                className="rounded-lg border border-border p-4"
                style={{ backgroundColor: 'var(--input)' }}
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-primary/15 text-primary text-sm font-bold mb-2">
                  {item.step}
                </span>
                <h3 className="text-sm font-medium text-foreground">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-muted-foreground">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </SeoSection>

        {/* About / value proposition */}
        <SeoSection
          id="small-business-invoicing"
          icon={Zap}
          title="Why Use Invo?"
          isOpen={!!openSections['small-business-invoicing']}
          onToggle={() => toggleSection('small-business-invoicing')}
        >
          <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-3">
              <p className="text-sm leading-6 text-muted-foreground">
                People searching for an <strong>invoice generator</strong>,{' '}
                <strong>invoice maker</strong>,{' '}
                <strong>PDF invoice generator</strong>, or{' '}
                <strong>small business invoicing tool</strong> usually need to solve
                one immediate problem: create a professional invoice and send it to a
                client today. Invo is built around that exact workflow — a live
                invoice preview, flexible templates, configurable payment details, and
                instant PDF export from one browser-based tool.
              </p>
              <p className="text-sm leading-6 text-muted-foreground">
                Whether you are a freelancer sending your first invoice, a consultant
                billing internationally, or a growing agency handling multiple client
                projects, Invo gives you a clean, fast invoicing experience without
                the overhead of a full accounting suite.
              </p>
            </div>

            <div
              className="rounded-lg border border-border p-4"
              style={{ backgroundColor: 'var(--input)' }}
            >
              <h3 className="text-sm font-medium text-foreground">
                Who uses Invo?
              </h3>
              <ul className="mt-3 space-y-2.5 text-sm leading-6 text-muted-foreground">
                {homepageUseCases.map((useCase) => (
                  <li key={useCase} className="flex gap-2.5">
                    <span
                      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                      aria-hidden
                    />
                    <span>{useCase}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </SeoSection>

        {/* FAQ */}
        <SeoSection
          id="invoice-generator-faqs"
          icon={HelpCircle}
          title="Frequently Asked Questions"
          isOpen={!!openSections['invoice-generator-faqs']}
          onToggle={() => toggleSection('invoice-generator-faqs')}
        >
          <div className="space-y-2">
            {homepageFaqs.map((faq, idx) => {
              const isActive = openFaq === idx;
              return (
                <div
                  key={faq.question}
                  className="rounded-lg border border-border overflow-hidden"
                  style={{ backgroundColor: 'var(--input)' }}
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isActive}
                    aria-controls={`faq-answer-${idx}`}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left group hover:bg-secondary/50 transition-colors duration-200"
                  >
                    <h3 className="text-sm font-medium text-foreground leading-snug">
                      {faq.question}
                    </h3>
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 transition-colors duration-200 text-muted-foreground group-hover:text-[#edff00] ${
                        isActive ? '' : 'rotate-[-90deg]'
                      }`}
                      aria-hidden
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        id={`faq-answer-${idx}`}
                        role="region"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{
                          duration: 0.2,
                          ease: 'easeInOut',
                        }}
                        className="overflow-hidden"
                      >
                        <p className="px-4 pb-3 text-sm leading-6 text-muted-foreground">
                          {faq.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </SeoSection>

        {/* CTA */}
        <SeoSection
          id="start-creating"
          icon={ArrowUpRight}
          title="Start Creating Invoices"
          isOpen={!!openSections['start-creating']}
          onToggle={() => toggleSection('start-creating')}
        >
          <p className="text-sm leading-6 text-muted-foreground max-w-3xl">
            Scroll up and start building your first invoice. Invo is free, works in
            your browser, and requires no account. Choose a template, fill in your
            details, and download your professional PDF invoice in minutes.
          </p>
        </SeoSection>
      </div>
    </section>
  );
};

export default SeoLandingContent;
